import { CONDITION_TYPES, isUnsupportedCondition } from './conditionTypes'

interface TargetingContext {
  _window: any,
  cookies?: Record<string, any>,
  enrichment?: any,
  embed: Record<string, any>,
  geo: Record<string, any>
}

interface EvaluateTargetingArgs {
  context: TargetingContext,
  conditions: any[]
}

// the actual evaluator for each specific condition
const evaluateSingleCondition = async (condition, context) => {
  // If we don't support the condition, say we matched since we have no knowledge of otherwise
  if(isUnsupportedCondition(condition)) {
    return Promise.resolve({
      conditionEvaluation: true,
      conditionEvalDetail: {
        evaluatedCondition: condition,
        matched: true,
        status: "EVALUATED"
      }
    })
  }

  const conditionEvaluatorDefinition = CONDITION_TYPES[condition.field]
  const conditionMatchEvaluator = conditionEvaluatorDefinition.operands[condition.operator]
  const conditionValue = !!conditionEvaluatorDefinition.transformValueFn ?
                            conditionEvaluatorDefinition.transformValueFn(condition.value) :
                            condition.value

  const conditionEvaluationState = await conditionEvaluatorDefinition.selector(context, condition)
  const conditionEvaluation = conditionMatchEvaluator(conditionValue, conditionEvaluationState)

  // log the details with wich the condition was evaluated for use in the TAL.
  const conditionEvalDetail = {
    evaluatedCondition: condition,
    matched: conditionEvaluation,
    actualValue: conditionEvaluationState,
    status: conditionEvaluatorDefinition.async
      ? (conditionEvaluation
          ? "EVALUATED"
          : "PENDING")
      : "EVALUATED"
  }

  return Promise.resolve({
    conditionEvaluation,
    conditionEvalDetail
  })
}

// given a condition type definition,
// return an evaluator function
const conditionEvaluator = (conditionDefinition) => (context: Record<string, any>) => (
  evaluateSingleCondition(conditionDefinition, context)
)

// given a list of conditions (like from a single campaign) evaluate until
// we are certain we can return matched and/or a set of async listeners
export const evaluateTargetingConditions = async ({
  context,
  conditions
}: EvaluateTargetingArgs) => {
  let matched = false
  let asyncConditionListeners = []
  let evalLog = []

  const evaluateConditions = async (group) => {
    let fullGroupMatch = true
    let groupEval = []

    // each item in a group is considered an "and"
    // all must pass for there to be a successfull match
    // the one exception is "async" types which can return a
    // positive eval in a listener (time on page, scroll pct etc..)
    for(let conditionIndex in group) {
      let conditionDefinition = group[conditionIndex]

      let {
        conditionEvalDetail,
        conditionEvaluation
      } = await evaluateSingleCondition(conditionDefinition, context)

      groupEval.push(conditionEvalDetail)

      if(!conditionEvaluation && !isUnsupportedCondition(conditionEvalDetail.evaluatedCondition)) {
        const {
          async = false,
          eventType = null,
          timer = false,
          selector,
          eventProperties = []
        } = CONDITION_TYPES[conditionDefinition.field]

        fullGroupMatch = false

        if(async) {
          asyncConditionListeners.push({
            conditionDefinition,
            selector,
            eventType,
            timer,
            eventProperties,
            type: conditionDefinition.field,
            evaluator: conditionEvaluator(conditionDefinition)
          })
        } else {
          asyncConditionListeners = []
          break
        }
      }
    }

    return Promise.resolve({
      fullGroupMatch,
      groupEval: {
        targetingConditions: groupEval,
        status: asyncConditionListeners.length
          ? "PENDING"
          : "EVALUATED"
      }
    })
  }

  // groups are the upper array which
  // are evaluated as "or's" - if one full set matches we can return a
  // successfull match
  for(let group of conditions) {
    let {
      fullGroupMatch,
      groupEval
    } = await evaluateConditions(group)

    evalLog.push(groupEval)

    if(fullGroupMatch) {
      matched = true
      asyncConditionListeners = []
      break
    }
  }

  return Promise.resolve({
    matched,
    asyncConditionListeners,
    evalLog
  })
}
